import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';
import { Providers } from './providers/providers';
import { useAuth } from './contexts/AuthContext';
import './globals.css';

// Lazy load components for better performance
const LoginPage = React.lazy(() => import('./pages/Login'));
const TablesPage = React.lazy(() => import('./pages/Tables'));
const TableViewPage = React.lazy(() => import('./pages/TableView'));
const PublicTableView = React.lazy(() => import('./components/Tables/PublicTableView'));

// Implement a loading component
const LoadingComponent = () => (
  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
    <CircularProgress />
  </Box>
);

// Private route component
const PrivateRoute = ({ children }: { children: React.ReactNode }) => {
  const { user, loading } = useAuth();

  if (loading) return <LoadingComponent />;

  return user ? <>{children}</> : <Navigate to="/login" replace />;
};

// Smart table route that checks if table is public before requiring auth
const SmartTableRoute = ({ children }: { children: React.ReactNode }) => {
  const { user, loading } = useAuth();
  
  if (loading) return <LoadingComponent />;
  
  // If user is authenticated, show the authenticated table view
  if (user) {
    return <>{children}</>;
  }
  
  // If not authenticated, show the public table view component
  // which will handle checking if the table is publicly accessible
  return <PublicTableView />;
};

function AppRoutes() {
  return (
    <React.Suspense fallback={<LoadingComponent />}>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route
          path="/tables"
          element={
            <PrivateRoute>
              <TablesPage />
            </PrivateRoute>
          }
        />
        <Route
          path="/table/:tableId"
          element={
            <SmartTableRoute>
              <TableViewPage />
            </SmartTableRoute>
          }
        />
        <Route path="/" element={<Navigate to="/tables" replace />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    </React.Suspense>
  );
}

function App() {
  return (
    <Providers>
      <AppRoutes />
    </Providers>
  );
}

export default App; 